import { useState, useCallback, useEffect } from 'react';
import apiClient from '../lib/api/apiClient';

export interface FranchiseTagSalaries {
  [position: string]: number;
  qualifyingOfferSalary: number;
}

interface UseFranchiseTagSalariesResult {
  salaries: FranchiseTagSalaries | null;
  loading: boolean;
  error: string | null;
  getSalaryByPosition: (position: string | null | undefined) => number | null;
  refetch: () => Promise<void>;
}

// Module-level cache to persist across component remounts
let salariesCache: FranchiseTagSalaries | null = null;
let isLoadingCache = false;

export const useFranchiseTagSalaries = (): UseFranchiseTagSalariesResult => {
  const [salaries, setSalaries] = useState<FranchiseTagSalaries | null>(salariesCache);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSalaries = useCallback(async () => {
    // If already loading, don't start another request
    if (isLoadingCache) {
      return;
    }

    // If we have cached data, use it
    if (salariesCache) {
      setSalaries(salariesCache);
      return;
    }

    setLoading(true);
    isLoadingCache = true;
    setError(null);

    try {
      const response = await apiClient.get('/player/franchise-tag-salaries');
      const data = response.data as FranchiseTagSalaries;
      salariesCache = data;
      setSalaries(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch franchise tag salaries';
      console.error('Error fetching franchise tag salaries:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
      isLoadingCache = false;
    }
  }, []);

  // Fetch on mount if not cached
  useEffect(() => {
    if (!salariesCache) {
      fetchSalaries();
    } else {
      setSalaries(salariesCache);
    }
  }, [fetchSalaries]);

  const getSalaryByPosition = useCallback(
    (position: string | null | undefined): number | null => {
      if (!salaries || !position) {
        return null;
      }

      // Normalize position to match API response keys
      const normalizedPosition = position.trim().toUpperCase();

      // Direct lookup
      if (salaries[normalizedPosition]) {
        return salaries[normalizedPosition];
      }

      // Handle position mappings
      // Map "CA" (catcher) to "C" if "CA" not found
      if (normalizedPosition === 'CA' && salaries['C']) {
        return salaries['C'];
      }
      // Map "C" to "CA" if "C" not found
      if (normalizedPosition === 'C' && salaries['CA']) {
        return salaries['CA'];
      }
      // Map "IF" (infield) to "INF" if "IF" not found
      if (normalizedPosition === 'IF' && salaries['INF']) {
        return salaries['INF'];
      }
      // Map "INF" to "IF" if "INF" not found
      if (normalizedPosition === 'INF' && salaries['IF']) {
        return salaries['IF'];
      }

      // The API returns positions like "C", "P", "1B", "2B", "3B", "SS", "CA", "CF", "DH", "LF", "OF", "RF", "LRF", "RP", "SP", "INF"
      return null;
    },
    [salaries]
  );

  return {
    salaries,
    loading,
    error,
    getSalaryByPosition,
    refetch: fetchSalaries,
  };
};

